import { useCallback, useRef, useState, type DragEvent, type ChangeEvent, type RefObject } from 'react';
import { CloudUpload } from 'lucide-react';
import { MAX_FILE_SIZE, CHUNK_SIZE } from '../config';

interface UploadZoneProps {
  onFileSelect: (file: File) => void;
  isUploading: boolean;
  fileInputRef?: RefObject<HTMLInputElement>;
}

export function UploadZone({ onFileSelect, isUploading, fileInputRef }: UploadZoneProps) {
  const internalRef = useRef<HTMLInputElement>(null);
  const inputRef = fileInputRef ?? internalRef;
  const [dragging, setDragging] = useState(false);

  const handleFiles = useCallback(
    (fileList: FileList | null) => {
      if (!fileList || !fileList.length) return;
      const file = fileList[0];
      onFileSelect(file);
    },
    [onFileSelect],
  );

  const onDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setDragging(false);
      handleFiles(event.dataTransfer.files);
    },
    [handleFiles],
  );

  return (
    <div
      className={`glass-panel relative flex flex-col items-center gap-4 rounded-3xl border border-dashed border-white/20 px-8 py-12 text-center transition ${dragging ? 'border-brand bg-brand/10' : ''}`}
      onDragEnter={(e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragOver={(e: DragEvent<HTMLDivElement>) => e.preventDefault()}
      onDragLeave={(e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setDragging(false);
      }}
      onDrop={onDrop}
      onClick={() => inputRef.current?.click()}
    >
      <CloudUpload className="h-12 w-12 text-brand" />
      <div>
        <p className="text-lg font-semibold text-white">拖拽文件至此或点击选择</p>
        <p className="text-sm text-white/60">
          支持大文件上传，单文件不超过 {Math.round(MAX_FILE_SIZE / 1024 / 1024 / 1024)} GB
        </p>
        <p className="text-xs text-white/40">
          大于 {Math.round(CHUNK_SIZE * 2 / 1024 / 1024)} MB 的文件将自动分片上传，支持断点续传
        </p>
      </div>
      <button className="rounded-full border border-white/20 px-6 py-2 text-sm text-white/70 hover:text-white" disabled={isUploading}>
        {isUploading ? '上传中…' : '选择文件'}
      </button>
      <input
        ref={inputRef}
        type="file"
        hidden
        onChange={(event: ChangeEvent<HTMLInputElement>) => handleFiles(event.target.files)}
      />
      <p className="text-xs text-white/50">
        支持断点续传，网络中断后可恢复上传
      </p>
    </div>
  );
}
